import { App, type AppState } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNative } from './platform';

// Biometric app-lock + lock-on-background (P0-SEC-02 / SEC-0.2).
//
// Two responsibilities:
//  1) Biometric gate — fingerprint / face / device credential via
//     @aparajita/capacitor-biometric-auth@8 (the Capacitor-6 major; a real dependency in
//     package.json). It is loaded through a DYNAMIC import gated on isNative() so the web bundle
//     never fetches or executes the plugin chunk — the website and `vite dev` are byte-for-byte
//     unaffected at runtime.
//  2) Lock manager — toggles a single UI "locked" flag when the app returns from the background beyond
//     a timeout. It is a UI flag ONLY: it must NEVER cancel in-flight work (background uploads,
//     recording, transcription). The shell renders a lock overlay off getLocked()/subscribe(),
//     and unlock() runs the biometric check.
//
// FAIL-CLOSED vs FAIL-OPEN (the security contract):
//   - Plugin present + a prompt can be shown + the user cancels/fails  -> FAIL CLOSED (stay locked;
//     the shell offers "Try again").
//   - Not native / plugin genuinely absent from the build              -> fail open (web must never
//     be blocked behind a prompt that can't render).
//   - Device has NO way to authenticate (no biometric hardware or enrolment AND no PIN/pattern —
//     checkBiometry() reports !isAvailable && !deviceIsSecure)         -> fail open (a device with
//     no credentials must not be bricked).
//   allowDeviceCredential: true means an un-enrolled-biometrics device with a PIN still gets a
//   real (fail-closed) lock via the system credential prompt.

type BiometricModule = typeof import('@aparajita/capacitor-biometric-auth');

let biometricCache: BiometricModule | null | undefined; // undefined = not yet attempted, null = absent.

/** Resolve the biometric plugin module once, or null when not native / import fails. */
async function loadBiometric(): Promise<BiometricModule | null> {
  if (!isNative()) return null;
  if (biometricCache !== undefined) return biometricCache;
  try {
    // Dynamic import keeps the plugin out of the web entry chunk; it only ever loads on device.
    biometricCache = await import('@aparajita/capacitor-biometric-auth');
  } catch {
    biometricCache = null;
  }
  return biometricCache;
}

/** How (and whether) this device can authenticate the app-lock. */
export interface BiometryStatus {
  /** True when a real lock prompt can be shown (biometry OR device PIN/pattern/password). */
  canAuthenticate: boolean;
  /** True when biometric hardware is present and enrolled. */
  biometryAvailable: boolean;
  /** True when the device has a PIN/pattern/password (device-credential fallback). */
  deviceIsSecure: boolean;
}

const NO_BIOMETRY: BiometryStatus = {
  canAuthenticate: false,
  biometryAvailable: false,
  deviceIsSecure: false,
};

/** Live device capability. On web / plugin-missing this is all-false (nothing to authenticate with). */
export async function getBiometryStatus(): Promise<BiometryStatus> {
  const mod = await loadBiometric();
  if (!mod) return NO_BIOMETRY;
  try {
    const res = await mod.BiometricAuth.checkBiometry();
    const biometryAvailable = !!res.isAvailable;
    const deviceIsSecure = !!res.deviceIsSecure;
    return {
      canAuthenticate: biometryAvailable || deviceIsSecure,
      biometryAvailable,
      deviceIsSecure,
    };
  } catch {
    return NO_BIOMETRY;
  }
}

/** True only when running native AND the plugin reports enrolled biometric hardware. */
export async function isBiometricAvailable(): Promise<boolean> {
  return (await getBiometryStatus()).biometryAvailable;
}

/**
 * BiometryError codes that mean the device genuinely cannot authenticate (rather than the user
 * failing/cancelling a shown prompt). Only these fail OPEN; everything else fails CLOSED.
 */
function isUnavailableError(mod: BiometricModule, err: unknown): boolean {
  if (!(err instanceof mod.BiometryError)) return false;
  const t = mod.BiometryErrorType;
  return (
    err.code === t.biometryNotAvailable ||
    err.code === t.biometryNotEnrolled ||
    err.code === t.noDeviceCredential ||
    err.code === t.passcodeNotSet
  );
}

/**
 * Prompt for biometric / device-credential auth. Resolves true on success.
 * FAILS OPEN (true) only when: not native / plugin absent, or the device has no biometrics AND no
 * device credential (nothing to prompt with — the app must not be bricked).
 * FAILS CLOSED (false) whenever a prompt was possible and the user cancelled or failed it.
 */
export async function authenticate(reason = 'Unlock CBAM'): Promise<boolean> {
  const mod = await loadBiometric();
  if (!mod) return true; // fail open: web/dev — no prompt could ever render
  const status = await getBiometryStatus();
  if (!status.canAuthenticate) return true; // fail open: no biometrics AND no device credential
  try {
    await mod.BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Cancel',
      // Device PIN/pattern/password as fallback so weak/no-enrolment devices still get a real lock.
      allowDeviceCredential: true,
      androidTitle: 'CBAM locked',
      androidSubtitle: reason,
    });
    return true;
  } catch (err) {
    // Belt-and-braces: if the prompt itself reports "nothing to authenticate with", fail open —
    // otherwise (cancel / failed attempt / lockout / system cancel) FAIL CLOSED.
    return isUnavailableError(mod, err);
  }
}

// ---------------------------------------------------------------------------------------------------
// Lock manager
// ---------------------------------------------------------------------------------------------------

export interface AppLockOptions {
  /** Lock only after the app has been backgrounded for at least this long (ms). Default 60_000. */
  timeoutMs?: number;
  /** Start locked so a cold start requires biometric before the UI is shown. Default true on native. */
  lockOnStart?: boolean;
}

type LockListener = (locked: boolean) => void;

let locked = false;
const listeners = new Set<LockListener>();
let backgroundedAt: number | null = null;
let timeoutMs = 60_000;
let stateHandle: PluginListenerHandle | null = null;

function setLocked(next: boolean): void {
  if (locked === next) return;
  locked = next;
  for (const cb of listeners) {
    try {
      cb(locked);
    } catch {
      /* a misbehaving subscriber must not break the dispatcher */
    }
  }
}

/** Current lock state (a UI flag only — never reflects in-flight transfer/recording state). */
export function getLocked(): boolean {
  return locked;
}

/** Subscribe to lock-state changes. Returns an unsubscribe fn. Fires immediately with current state. */
export function subscribe(cb: LockListener): () => void {
  listeners.add(cb);
  try {
    cb(locked);
  } catch {
    /* ignore */
  }
  return () => {
    listeners.delete(cb);
  };
}

/**
 * Attempt to unlock via biometric. On success clears the locked flag and returns true; on failure the
 * app stays locked and returns false. Does NOT touch any background task.
 */
export async function unlock(reason = 'Unlock CBAM'): Promise<boolean> {
  const ok = await authenticate(reason);
  if (ok) setLocked(false);
  return ok;
}

/**
 * Initialise lock-on-background (P0-SEC-02). Native only — web is a no-op returning a no-op disposer.
 * Listens to @capacitor/app 'appStateChange': on entering background it timestamps; on returning to
 * the foreground it locks iff the gap exceeded timeoutMs. Backgrounding NEVER cancels in-flight work —
 * it only flips the UI lock flag. Returns a disposer that removes the listener and clears subscribers.
 */
export function initAppLock(opts: AppLockOptions = {}): () => void {
  if (!isNative()) return () => {};

  timeoutMs = opts.timeoutMs ?? 60_000;
  if (opts.lockOnStart ?? true) setLocked(true);

  const handler = (state: AppState) => {
    if (!state.isActive) {
      // Going to background: remember when, so we can measure the away-duration on return.
      backgroundedAt = Date.now();
      return;
    }
    // Returning to foreground: lock if we were away longer than the timeout.
    if (backgroundedAt != null && Date.now() - backgroundedAt >= timeoutMs) {
      setLocked(true);
    }
    backgroundedAt = null;
  };

  const subPromise = App.addListener('appStateChange', handler);
  void subPromise.then((h) => {
    stateHandle = h;
  });

  return () => {
    void subPromise.then((h) => h.remove().catch(() => {}));
    stateHandle = null;
    listeners.clear();
    backgroundedAt = null;
  };
}
